import { useContext, useState } from 'react';
import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { NavigationContext } from '@layout/navigation/navigation';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from '@hooks';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { inviteSendAdm } from '@services/queries/inviteSendAdm/inviteSendAdm';

import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Confirm,
  Line,
  Form,
  Divider,
  Label,
  InputContainer,
  InputSearch,
  TextButton,
  Text,
} from './InviteUserDialog.styles';
import { RoleOptionsMenu } from './Components/Menu/Menu';
import { RoleGuides } from './Components/RoleGuides/RoleGuides';

interface IInviteUserDialog {
  open: boolean;
  onClose: () => void;
}

export interface IFormData {
  email: string;
  role: string;
}

export const InviteUserDialog = ({ open, onClose }: IInviteUserDialog) => {
  const portalDiv = document.getElementById('portal');

  const queryClient = useQueryClient();

  const { studioId } = useContext(NavigationContext);
  const [openGuide, setOpenGuide] = useState<boolean>(false);

  const { toast } = useToast();

  const schema = yup.object().shape({
    email: yup.string().email().required(),
    role: yup.string().required(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { isValid },
    control,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const mutation = useMutation(inviteSendAdm, {
    onSuccess: () => {
      queryClient.invalidateQueries(['studios', studioId, 'invites']);
      toast.success('Invite sent');
      setValue('email', '');
      setValue('role', '');
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.data?.message === 'User already exists') {
        toast.error('User already exists');
      } else {
        toast.error('Something went wrong');
      }
    },
  });

  const onSubmit = (invite: IFormData) => {
    if (!mutation.isLoading) {
      mutation.mutate({
        email: invite.email,
        role: invite.role.toLowerCase(),
        studioId,
      });
    }
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Invite User</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider height="28px" />

            <Form onSubmit={handleSubmit(onSubmit)}>
              <Label>Email</Label>

              <InputContainer>
                <InputSearch
                  placeholder="Type a valid email"
                  {...register('email')}
                  autoFocus
                />
              </InputContainer>

              <Divider height="24px" />

              <Label>Role</Label>

              <Controller
                control={control}
                name="role"
                render={({ field: { onChange, value } }) => (
                  <RoleOptionsMenu onChange={onChange} value={value} />
                )}
              />

              <Line>
                <Text>
                  Unsure about the roles? Check our{' '}
                  <TextButton
                    onClick={() => {
                      setOpenGuide(true);
                    }}
                  >
                    comprehensive role guide
                  </TextButton>
                </Text>

                <Confirm
                  type="submit"
                  disabled={!isValid || mutation.isLoading}
                >
                  Add
                </Confirm>
              </Line>
            </Form>

            <RoleGuides
              open={openGuide}
              onClose={() => {
                setOpenGuide(false);
              }}
            />
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
